import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CommonLayout from "layouts/DashboardLayout";
import { useUnsavedChangesRegistration } from "contexts/UnsavedChangesProvider";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MDButton from "components/MDButton";
import ImageDragger from "common/imageDragger";
import CommonQuillEditor from "common/editor";
import { openSnackbar } from "../../../../redux/slices/common";
import { fetchDistributors } from "../../../../redux/slices/distributor";
import { fetchBrands, uploadBrandImage } from "../../../../redux/slices/hyperwolfBrand";
import { getAllInfoEffect } from "../../../../redux/slices/hyperwolf/strains";
import { fetchProductCategoriesProduct } from "../../../../redux/slices/brand";
import {
  fetchProductTagsListingAPI,
  createProductTagAPI,
  createProductAPI,
  getProductDetailsForEditAPI,
  updateSingleProductAPI,
} from "../../../../redux/apis/hyperwolf/products";
import { uploadProductImageAPI } from "../../../../redux/apis/hyperwolf/productImages";
import { convertAndExtractMeta, getData } from "utilities/common";
import { buildProductMetaPrompt } from "prompt";

// Weight Per Unit (WPU) – label for display, value for API
const WPU_OPTIONS = [
  { label: "Each", value: "EACH" },
  { label: "Half Gram Unit", value: "HALF_GRAM_UNIT" },
  { label: "Whole Gram Unit", value: "FULL_GRAM" },
  { label: "Eighth Per Unit", value: "EIGHTH_PER_UNIT" },
  { label: "Custom Weight", value: "CUSTOM_GRAMS" },
];
const WPU_VALUES = WPU_OPTIONS.map((o) => o.value);

// Custom Gram Type – label for display, value for API (used when WPU is CUSTOM_GRAMS)
const CUSTOM_GRAM_TYPE_OPTIONS = [
  { label: "Gram", value: "GRAM" },
  { label: "Milligram", value: "MILLIGRAM" },
  { label: "Fluid Ounce", value: "FLUID_OUNCE" },
  { label: "Ounce", value: "OUNCE" },
];
const CUSTOM_GRAM_TYPE_VALUES = CUSTOM_GRAM_TYPE_OPTIONS.map((o) => o.value);

/** HTTP 200 body that still means save failed (e.g. `{ message: "Something is wrong!" }`). */
function isProductSaveErrorResponse(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  if (data.success === false) return true;
  if (typeof data.error === "string" && data.error.trim() !== "") return true;
  const keys = Object.keys(data);
  const msg = data.message;
  if (typeof msg !== "string" || msg.trim() === "") return false;
  if (keys.length === 1 && keys[0] === "message") return true;
  if (
    keys.length === 2 &&
    keys.includes("message") &&
    (keys.includes("statusCode") || keys.includes("status"))
  ) {
    return true;
  }
  return false;
}

function messageFromProductSaveError(data) {
  if (!data || typeof data !== "object") return "Request failed";
  const m = typeof data.message === "string" ? data.message.trim() : "";
  const e = typeof data.error === "string" ? data.error.trim() : "";
  return m || e || "Request failed";
}

function messageFromAxiosError(err) {
  const d = err?.response?.data;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    if (typeof d.message === "string" && d.message.trim()) return d.message.trim();
    if (typeof d.error === "string" && d.error.trim()) return d.error.trim();
    if (Array.isArray(d.errors)) return d.errors.map(String).filter(Boolean).join(", ");
  }
  if (err?.message && typeof err.message === "string") return err.message;
  return "Something went wrong. Please try again.";
}

const initialForm = {
  status: "Active",
  productImages: [],
  productName: "",
  retailPrice: "",
  strain: "",
  thcMgPercent: "",
  sku: "",
  categoryId: "",
  brandId: "",
  distributorId: "",
  weightPerUnit: "",
  customWeight: "",
  customGramType: "GRAM",
  sellType: "Both",
  // cannabisType: "",
  tags: [],
  description: "",
  // FAQs Section
  faqSectionTitle: "Faq's",
  faqs: [],
  // Meta Properties
  metaTitle: "",
  metaDescription: "",
  // Content Section
  content: "",
};

/** Stable snapshot for dirty detection (files, assets, text fields). */
function serializeProductFormSnapshot(data) {
  const images = (data.productImages || []).map((img) => {
    if (img?.file) {
      const f = img.file;
      return `new:${f.name}:${f.size}:${f.lastModified}`;
    }
    if (img?.asset) {
      const a = img.asset;
      const id = a._id ?? a.id ?? a.assetId ?? "";
      return id ? `asset:${id}` : `asset:${String(a.publicURL ?? a.mediumURL ?? "")}`;
    }
    const p = img?.preview;
    return p ? `preview:${typeof p === "string" ? p : String(p)}` : "";
  });
  const faqs = (data.faqs || []).map((f) => ({
    i: f?._id,
    q: f?.question ?? "",
    a: f?.answer ?? "",
  }));
  const sortedTags = [...(data.tags || [])].map(String).sort();
  return JSON.stringify({
    status: data.status ?? "",
    productName: data.productName ?? "",
    retailPrice: String(data.retailPrice ?? ""),
    strain: data.strain ?? "",
    thcMgPercent: String(data.thcMgPercent ?? ""),
    sku: data.sku ?? "",
    categoryId: data.categoryId ?? "",
    brandId: data.brandId ?? "",
    distributorId: data.distributorId ?? "",
    weightPerUnit: data.weightPerUnit ?? "",
    customWeight: String(data.customWeight ?? ""),
    customGramType: data.customGramType ?? "",
    sellType: data.sellType ?? "",
    tags: sortedTags,
    description: data.description ?? "",
    faqSectionTitle: data.faqSectionTitle ?? "",
    faqs,
    metaTitle: data.metaTitle ?? "",
    metaDescription: data.metaDescription ?? "",
    content: data.content ?? "",
    productImages: images,
  });
}

const STILO_GENERATE_ENDPOINT = "/api/v1/generate/description";
const STILO_API_BASE_URL = process.env.REACT_APP_STILO_API_BASE_URL || "";
const STILO_API_KEY = process.env.REACT_APP_API_AUTH_KEY || "";

// Theme uses borderRadius.section (10rem) for pill-shaped inputs
const productInfoFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10rem",
    minHeight: "40px",
    height: "40px",
    "& fieldset": { borderRadius: "10rem" },
  },
  "& .MuiInputBase-root": {
    minHeight: "40px",
    height: "40px",
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    minHeight: "40px",
    height: "40px",
    boxSizing: "border-box",
    paddingTop: 0,
    paddingBottom: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiInputLabel-outlined": {
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
  },
  // Show dropdown chevron (theme hides it globally)
  "& .MuiSelect-icon": {
    display: "block !important",
    color: "rgba(0, 0, 0, 0.54)",
    right: 12,
  },
};

// Tags: multi-value autocomplete with inline chips.
// Intentionally NOT pill-shaped (10rem) — a fixed 40px pill clips the
// input text and breaks as soon as chips wrap. 14px radius stays clean
// for both single-line and multi-line states.
const tagsAutocompleteSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    minHeight: "44px",
    height: "auto",
    padding: "6px 46px 6px 12px !important",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
    "& fieldset": {
      borderRadius: "14px",
    },
  },
  // Typing area: grows naturally, never clipped, always obvious where to type
  "& .MuiAutocomplete-input": {
    flex: "1 1 auto",
    minWidth: "140px !important",
    width: "auto !important",
    padding: "6px 4px !important",
    height: "28px",
    boxSizing: "border-box",
  },
  // Inline selected tags
  "& .MuiChip-root": {
    height: "28px",
    margin: 0,
    borderRadius: "8px",
    bgcolor: "#F1F2F6",
    border: "1px solid #E3E5EC",
    color: "#2E2E3A",
    fontWeight: 500,
    fontSize: "0.8125rem",
    "& .MuiChip-label": {
      padding: "0 2px 0 8px",
    },
    "& .MuiChip-deleteIcon": {
      color: "#6B7280",
      fontSize: "16px",
      margin: "0 6px 0 -2px",
      "&:hover": { color: "#D32F2F" },
    },
  },
  // Keep chevron + clear pinned to the first row when the field grows
  "& .MuiAutocomplete-endAdornment": {
    right: 10,
    top: 22,
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  "& .MuiAutocomplete-popupIndicator": {
    display: "flex !important",
    alignItems: "center",
    justifyContent: "center",
    color: "rgba(0, 0, 0, 0.54)",
    padding: 2,
  },
  "& .MuiAutocomplete-clearIndicator": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 2,
  },
};

const roundedMultilineSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10rem",
    alignItems: "flex-start",
    "& fieldset": { borderRadius: "10rem" },
  },
};

function getProductFormModalClassName() {
  try {
    const raw = getData("adminsMode");
    return raw && JSON.parse(raw) === "hyperdrive" ? "bg-black cus-modal" : "cus-modal";
  } catch {
    return "cus-modal";
  }
}

export default function AddProductHyperwolf() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { productId } = useParams();
  const isEditMode = Boolean(productId);
  const { brandsList: hyperwolfBrandsList = [] } = useSelector(
    (state) => state.hyperwolfBrand ?? {}
  );
  const { distributorsList: distributorsRaw = [] } = useSelector(
    (state) => state.distributor ?? {}
  );
  const { infoEffects = [] } = useSelector((state) => state.hyperwolfStrains ?? {});
  const distributorsList = Array.isArray(distributorsRaw) ? distributorsRaw : [];
  const strainTypeOptions = Array.isArray(infoEffects) ? infoEffects : [];
  const brandsList = Array.isArray(hyperwolfBrandsList)
    ? hyperwolfBrandsList
    : [];

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [tagOptions, setTagOptions] = useState([]);
  const [tagSearchLoading, setTagSearchLoading] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [fetchProductLoading, setFetchProductLoading] = useState(() => Boolean(productId));
  const [generateLoading, setGenerateLoading] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const tagSearchTimeoutRef = useRef(null);
  const baselineSerializedRef = useRef("");
  const [baselineReady, setBaselineReady] = useState(false);

  const dirty = useMemo(() => {
    if (fetchProductLoading || !baselineReady) return false;
    return serializeProductFormSnapshot(formData) !== baselineSerializedRef.current;
  }, [formData, fetchProductLoading, baselineReady]);

  useEffect(() => {
    if (fetchProductLoading) {
      setBaselineReady(false);
      return;
    }
    // Create mode: set baseline from initial form once.
    // Edit mode baseline is set after we populate formData from API.
    if (!baselineReady && !productId) {
      baselineSerializedRef.current = serializeProductFormSnapshot(formData);
      setBaselineReady(true);
    }
  }, [fetchProductLoading, productId, baselineReady, formData]);

  const pendingLeavePathRef = useRef("/hyperwolf/products");

  const navigateToProductsList = useCallback(() => {
    const q = location.state?.productsListQuery;
    if (q != null && String(q).trim() !== "") {
      navigate(`/hyperwolf/products?${q}`);
      return;
    }
    const s = location.state?.productsListSearch;
    if (s != null && String(s).trim() !== "") {
      navigate(`/hyperwolf/products?search=${encodeURIComponent(String(s).trim())}`);
    } else {
      navigate("/hyperwolf/products");
    }
  }, [location.state, navigate]);

  const confirmLeaveWithoutSaving = useCallback(() => {
    if (pendingLeavePathRef.current === "/hyperwolf/products") {
      navigateToProductsList();
    } else {
      navigate(pendingLeavePathRef.current);
    }
  }, [navigate, navigateToProductsList]);

  const { promptLeave } = useUnsavedChangesRegistration({
    isDirty: dirty,
    blocked: fetchProductLoading,
    formPath: `${location.pathname}${location.search}${location.hash}`,
    onConfirmLeave: confirmLeaveWithoutSaving,
  });

  const requestNavigateAway = (path = "/hyperwolf/products") => {
    if (!dirty) {
      if (path === "/hyperwolf/products") {
        navigateToProductsList();
      } else {
        navigate(path);
      }
      return;
    }
    pendingLeavePathRef.current = path;
    promptLeave();
  };

  const normalizeWpuFromEdit = (wpu, customGramType) => {
    if (wpu && WPU_VALUES.includes(wpu)) return wpu;
    if (customGramType === "GRAM") return "FULL_GRAM";
    if (customGramType === "MILLIGRAM") return "CUSTOM_GRAMS";
    if (customGramType === "OUNCE") return "OUNCE";
    if (customGramType === "FLUID_OUNCE") return "FLUID_OZ";
    if (customGramType === "MILLILITER") return "CUSTOM_GRAMS";
    return "";
  };

  const validateForm = () => {
    const nextErrors = {};
    const name = (formData.productName || "").trim();
    if (!name) nextErrors.productName = "Product name is required";
    if (!formData.categoryId) nextErrors.categoryId = "Category is required";
    if (!formData.brandId) nextErrors.brandId = "Brand is required";
    if (!formData.distributorId) nextErrors.distributorId = "Distributor is required";
    const price = formData.retailPrice;
    if (price !== "" && price != null) {
      const num = Number(price);
      if (Number.isNaN(num) || num < 0) nextErrors.retailPrice = "Enter a valid price (0 or more)";
    }
    if (formData.weightPerUnit === "CUSTOM_GRAMS") {
      const customWeight = (formData.customWeight ?? "").trim();
      if (!customWeight) nextErrors.customWeight = "Custom weight is required";
    }
    setErrors(nextErrors);
    return { isValid: Object.keys(nextErrors).length === 0, errors: nextErrors };
  };

  useEffect(() => {
    dispatch(fetchBrands({ skip: 0, limit: 500, status: "Active" }));
    dispatch(
      fetchDistributors({
        skip: 0,
        limit: 500,
        sortBy: "distributorName",
        order: "asc",
        status: "Active",
      })
    );
    dispatch(getAllInfoEffect());
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        const res = await dispatch(
          fetchProductCategoriesProduct({
            platform: "hyperwolf",
            skip: 0,
            limit: 500,
            userType: "admin",
          })
        ).unwrap();
        setCategories(Array.isArray(res?.categories) ? res.categories : []);
      } catch (e) {
        setCategories([]);
      }
    })();
  }, [dispatch]);

  useEffect(() => {
    if (!productId) return;
    setFetchProductLoading(true);
    getProductDetailsForEditAPI(productId)
      .then((res) => {
        const raw = res?.data ?? res;
        if (!raw) return;
        const details = raw.productDetails ?? raw;
        const d = details.productData ?? details;
        const assets = Array.isArray(d.assets) ? d.assets : [];
        const images =
          assets.length > 0
            ? assets.map((a) => a.publicURL ?? a.origURL ?? a.largeURL ?? a.mediumURL)
            : d.images ?? d.productImages ?? [];
        const imageItems =
          assets.length > 0
            ? assets.map((a) => ({
              preview: a.publicURL ?? a.origURL ?? a.largeURL ?? a.mediumURL,
              asset: a,
            }))
            : Array.isArray(images)
              ? images.map((url) => (typeof url === "string" ? { preview: url } : { preview: url?.url ?? url?.preview }))
              : [];

        const nextForm = {
          status: d.active === true || d.active === "true" ? "Active" : "Inactive",
          productImages: imageItems,
          productName: d.name ?? details.adminProductName ?? details.websiteProductName ?? "",
          retailPrice: d.unitPrice != null ? String(d.unitPrice) : "",
          strain: d.flowerType ?? details.strainType ?? "",
          thcMgPercent: d.thc != null ? String(d.thc) : d.thcMgPercent ?? "",
          sku: d.sku ?? details.sku ?? details.productBatchSKU ?? "",
          categoryId: d.categoryId ?? "",
          brandId: d.brandId ?? "",
          distributorId: d.vendorId ?? d.distributorId ?? "",
          weightPerUnit: (d.weightPerUnit && WPU_VALUES.includes(d.weightPerUnit) ? d.weightPerUnit : normalizeWpuFromEdit(d.weightPerUnit, d.customGramType)) || "",
          customWeight: d.customWeight != null ? String(d.customWeight) : d.unitValue != null ? String(d.unitValue) : "",
          customGramType: CUSTOM_GRAM_TYPE_VALUES.includes(d.customGramType) ? d.customGramType : "GRAM",
          sellType: d.productSaleType ?? "Both",
          // cannabisType: d.cannabisType ?? details.strainType ?? "",
          tags: Array.isArray(d.tags) ? d.tags : [],
          description: d.description ?? "",
          faqSectionTitle: "Faq's",
          faqs: (() => {
            const faqList = raw.faq ?? details.faq ?? d.faq;
            if (Array.isArray(faqList) && faqList.length > 0) {
              return faqList.map((f) => ({
                question: f?.question ?? "",
                answer: f?.answer ?? "",
                ...(f?._id && { _id: f._id }),
              }));
            }
            return [];
          })(),
          metaTitle: details.metaTitle ?? d.metaTitle ?? "",
          metaDescription: details.metaDescription ?? d.metaDescription ?? "",
          content: details.content ?? d.content ?? "",
        };
        setFormData(nextForm);
        baselineSerializedRef.current = serializeProductFormSnapshot(nextForm);
        setBaselineReady(true);
      })
      .catch((err) => console.error("Fetch product failed:", err))
      .finally(() => setFetchProductLoading(false));
  }, [productId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const removeImageHandler = (index) => {
    setFormData((prev) => {
      const next = prev.productImages.filter((_, i) => i !== index);
      const removed = prev.productImages[index];
      if (removed?.preview && typeof removed.preview === "string") URL.revokeObjectURL(removed.preview);
      return { ...prev, productImages: next };
    });
  };

  const productImageUploadHandler = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const newItems = Array.from(files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setFormData((p) => ({ ...p, productImages: [...p.productImages, ...newItems] }));
    e.target.value = "";
  };

  const productImageReorderHandler = (updatedPreviewUrls) => {
    setFormData((prev) => {
      const byPreview = new Map(prev.productImages.map((item) => [item.preview, item]));
      const reordered = updatedPreviewUrls.map((url) => byPreview.get(url)).filter(Boolean);
      return reordered.length === prev.productImages.length ? { ...prev, productImages: reordered } : prev;
    });
  };

  const fetchTagOptions = useCallback((search) => {
    setTagSearchLoading(true);
    const params = search && search.trim() ? { search: search.trim() } : {};
    fetchProductTagsListingAPI(params)
      .then((res) => {
        const data = res?.data;
        const list = Array.isArray(data) ? data : data?.tags ?? data?.values ?? data?.data ?? [];
        setTagOptions(Array.isArray(list) ? list.map((t) => (typeof t === "string" ? t : t?.tag ?? t?.name ?? "")).filter(Boolean) : []);
      })
      .catch(() => setTagOptions([]))
      .finally(() => setTagSearchLoading(false));
  }, []);

  useEffect(() => {
    if (tagSearchTimeoutRef.current) clearTimeout(tagSearchTimeoutRef.current);
    tagSearchTimeoutRef.current = setTimeout(() => {
      fetchTagOptions(tagInput);
    }, 300);
    return () => {
      if (tagSearchTimeoutRef.current) clearTimeout(tagSearchTimeoutRef.current);
    };
  }, [tagInput, fetchTagOptions]);

  const normalizeTagValue = (val) => (val || "").trim().replace(/,/g, "");

  const persistNewTagOption = useCallback(
    (val) => {
      const normalized = normalizeTagValue(val);
      if (!normalized) return;
      if (tagOptions.includes(normalized)) return;
      // Fire-and-forget: local UI updates immediately, API failure shouldn't block tagging
      createProductTagAPI({ tag: normalized }).catch(() => {});
    },
    [tagOptions]
  );

  // MUI multiple + controlled inputValue: a selection fires onChange AND
  // onInputChange(reason === "reset"). Clearing in both places prevents the
  // stale-text bug where the just-added tag lingered in the input.
  const handleTagsChange = (_, newValue) => {
    const normalized = [
      ...new Set(
        (Array.isArray(newValue) ? newValue : [])
          .map((v) => normalizeTagValue(typeof v === "string" ? v : v?.tag ?? v?.name ?? ""))
          .filter(Boolean)
      ),
    ];
    const added = normalized.filter((t) => !formData.tags.includes(t));
    added.forEach(persistNewTagOption);
    setFormData((prev) => ({ ...prev, tags: normalized }));
    setTagInput("");
  };

  const handleTagsInputChange = (_, newInput, reason) => {
    if (reason === "reset") {
      setTagInput("");
      return;
    }
    setTagInput(newInput ?? "");
  };

  const filterTagOptions = (options, params) => {
    const q = normalizeTagValue(params.inputValue).toLowerCase();
    const filtered = q
      ? options.filter((o) => String(o).toLowerCase().includes(q))
      : [...options];
    const trimmed = normalizeTagValue(params.inputValue);
    if (
      trimmed &&
      !filtered.some((o) => String(o).toLowerCase() === trimmed.toLowerCase()) &&
      !formData.tags.some((t) => String(t).toLowerCase() === trimmed.toLowerCase())
    ) {
      filtered.push(trimmed);
    }
    return filtered;
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      const val = normalizeTagValue(tagInput);
      if (!val) return;
      e.preventDefault();
      if (formData.tags.some((t) => t.toLowerCase() === val.toLowerCase())) {
        setTagInput("");
        return;
      }
      persistNewTagOption(val);
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, val] }));
      setTagInput("");
    }
  };

  const addFaq = () => {
    setFormData((prev) => ({
      ...prev,
      faqs: [...prev.faqs, { question: "", answer: "" }],
    }));
  };

  const removeFaq = (index) => {
    setFormData((prev) => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index),
    }));
  };

  const updateFaq = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      faqs: prev.faqs.map((faq, i) =>
        i === index ? { ...faq, [field]: value } : faq
      ),
    }));
  };

  const brands = Array.isArray(brandsList) ? brandsList : [];

  const getCustomGramType = () => {
    if (formData.weightPerUnit === "CUSTOM_GRAMS") {
      return CUSTOM_GRAM_TYPE_VALUES.includes(formData.customGramType) ? formData.customGramType : "GRAM";
    }
    return undefined;
  };

  const getProductSaleType = () => {
    const s = formData.sellType;
    if (s === "Retail") return "Retail";
    if (s === "Wholesale") return "Wholesale";
    return "Both";
  };

  const getSelectedCategoryName = () => {
    const cat = categories.find(
      (x) => (x.categoryId || x.id || x._id) === formData.categoryId
    );
    return cat?.name ?? cat?.categoryName ?? "";
  };

  const getSelectedBrandName = () => {
    const b = brands.find((x) => (x.brandId || x._id || x.id) === formData.brandId);
    return b?.brandName ?? b?.name ?? "";
  };

  const getWeightInfo = () => {
    if (formData.weightPerUnit === "CUSTOM_GRAMS") {
      const value = String(formData.customWeight ?? "").trim();
      const unit = String(getCustomGramType() ?? formData.customGramType ?? "").trim();
      return { value, unit };
    }
    const label = WPU_OPTIONS.find((o) => o.value === formData.weightPerUnit)?.label || "";
    return { value: "1", unit: label };
  };

  const getThcInfo = () => {
    const raw = String(formData.thcMgPercent ?? "").trim();
    if (!raw) return { value: "", unit: "" };
    if (raw.includes("%")) return { value: raw.replace("%", "").trim(), unit: "%" };
    return { value: raw, unit: "mg" };
  };

  const canGenerateMeta = (() => {
    if (isEditMode) return false;
    const productName = String(formData.productName ?? "").trim();
    const categoryName = String(getSelectedCategoryName() ?? "").trim();
    const brandName = String(getSelectedBrandName() ?? "").trim();
    const strainType = String(formData.strain ?? "").trim();
    const weight = getWeightInfo();
    const thc = getThcInfo();
    return Boolean(
      productName &&
      categoryName &&
      brandName &&
      strainType &&
      String(weight.value ?? "").trim() &&
      String(weight.unit ?? "").trim() &&
      String(thc.value ?? "").trim() &&
      String(thc.unit ?? "").trim() &&
      STILO_API_BASE_URL &&
      STILO_API_KEY
    );
  })();

  const handleGenerateMeta = async () => {
    if (!canGenerateMeta || generateLoading) return;
    setGenerateLoading(true);
    try {
      setFormData((p) => ({
        ...p,
        metaTitle: "",
        metaDescription: "",
        content: "",
      }));
      const productName = String(formData.productName ?? "").trim();
      const categoryName = String(getSelectedCategoryName() ?? "").trim();
      const brandName = String(getSelectedBrandName() ?? "").trim();
      const strainType = String(formData.strain ?? "").trim();
      const weight = getWeightInfo();
      const thc = getThcInfo();

      const prompt = buildProductMetaPrompt({
        productName,
        brandName,
        categoryName,
        thcValue: thc.value,
        thcUnit: thc.unit,
        strainType,
        weightValue: weight.value,
        weightUnit: weight.unit,
      });

      const res = await fetch(`${STILO_API_BASE_URL}${STILO_GENERATE_ENDPOINT}`, {
        method: "POST",
        body: JSON.stringify({ prompt }),
        headers: {
          "Content-Type": "application/json",
          "x-api-key": STILO_API_KEY,
        },
      });

      if (!res?.body) throw new Error("No response body");

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullContent += chunk;

        const finalResult = convertAndExtractMeta(fullContent);
        setFormData((prev) => ({
          ...prev,
          metaTitle: finalResult.metaTitle || prev.metaTitle,
          metaDescription: finalResult.metaDescription || prev.metaDescription,
          content: finalResult.content || prev.content,
        }));
      }

      const finalResult = convertAndExtractMeta(fullContent);
      setFormData((prev) => ({
        ...prev,
        metaTitle: finalResult.metaTitle || prev.metaTitle,
        metaDescription: finalResult.metaDescription || prev.metaDescription,
        content: finalResult.content || prev.content,
      }));
    } catch (e) {
      console.error("Generate description failed:", e);
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleSaveProduct = () => {
    const { isValid } = validateForm();
    if (!isValid) return;
    setSaveConfirmOpen(true);
  };

  const executeSaveProduct = async () => {
    setSaveInProgress(true);
    let imageAssets = [];

    try {
      if (Array.isArray(formData.productImages) && formData.productImages.length > 0) {
        for (const item of formData.productImages) {
          if (item?.file) {
            const body = new FormData();
            body.append("images", item.file);
            body.append("name", formData.productName || item.file.name || "product-image");
            const res = await uploadProductImageAPI(body);
            if (res?.blazeAsset) {
              imageAssets.push(res.blazeAsset);
            }
          } else if (item?.asset) {
            imageAssets.push(item.asset);
          }
        }
      }

      const cat = categories.find(
        (x) => (x.categoryId || x.id || x._id) === formData.categoryId
      );
      const categoryName = cat?.name ?? cat?.categoryName ?? "";

      const vendorBlazeId = formData.distributorId || undefined;

      const payload = {
        categoryId: (cat?.categoryId ?? formData.categoryId) || undefined,
        categoryName: categoryName || undefined,
        vendorId: vendorBlazeId || undefined,
        brandId: formData.brandId || undefined,
        name: formData.productName || "",
        description: formData.description || "",
        flowerType: formData.strain || undefined,
        unitPrice: formData.retailPrice ? Number(formData.retailPrice) : undefined,
        showInWidget: true,
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        active: formData.status === "Active",
        weightPerUnit: formData.weightPerUnit || undefined,
        customGramType: getCustomGramType(),
        customWeight:
          formData.weightPerUnit === "CUSTOM_GRAMS" && formData.customWeight !== "" && formData.customWeight != null
            ? (() => {
              const v = String(formData.customWeight).trim();
              const num = Number(v);
              return !Number.isNaN(num) ? num : v;
            })()
            : undefined,
        productSaleType: getProductSaleType(),
        thc:
          formData.thcMgPercent !== "" &&
            formData.thcMgPercent != null &&
            !Number.isNaN(Number(formData.thcMgPercent))
            ? Number(formData.thcMgPercent)
            : 0,
        metaTitle: formData.metaTitle || "",
        metaDescription: formData.metaDescription || "",
        content: formData.content || "",
        faq: (formData.faqs || []).map((f) => {
          const item = { question: f?.question ?? "", answer: f?.answer ?? "" };
          if (f?._id) item._id = f._id;
          return item;
        }),
        sku: formData.sku ?? "",
      };
      if (imageAssets.length > 0) payload.assets = imageAssets;
      if (isEditMode && productId) payload.id = productId;

      const saveRes =
        isEditMode && productId
          ? await updateSingleProductAPI(productId, payload)
          : await createProductAPI(payload);
      const saveData = saveRes?.data;
      if (isProductSaveErrorResponse(saveData)) {
        dispatch(
          openSnackbar({
            content: messageFromProductSaveError(saveData),
            color: "error",
            icon: "warning",
          })
        );
        return;
      }
      baselineSerializedRef.current = serializeProductFormSnapshot(formData);
      setBaselineReady(true);
      navigateToProductsList();
    } catch (err) {
      console.error("Create product failed:", err);
      dispatch(
        openSnackbar({
          content: messageFromAxiosError(err),
          color: "error",
          icon: "warning",
        })
      );
    } finally {
      setSaveInProgress(false);
    }
  };

  const confirmSaveProduct = () => {
    setSaveConfirmOpen(false);
    void executeSaveProduct();
  };

  const pageLoading = fetchProductLoading || saveInProgress;

  const productFormDialogClassName = getProductFormModalClassName();
  const isHyperdriveDialog = productFormDialogClassName.includes("bg-black");
  const productDialogPaperSx = isHyperdriveDialog
    ? {
        borderRadius: "12px",
        overflow: "hidden",
      }
    : {
        borderRadius: "20px",
        overflow: "hidden",
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[200]}`,
        boxShadow: "0px 8px 32px rgba(46, 46, 58, 0.1)",
      };
  const productDialogBackdropSx = {
    backgroundColor: isHyperdriveDialog ? "rgba(0, 0, 0, 0.4)" : "rgba(46, 46, 58, 0.45)",
    backdropFilter: "blur(4px)",
  };
  const productDialogTitleSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    py: 2.5,
    px: 3,
    pb: 1.5,
    fontWeight: 600,
    fontSize: "1.25rem",
    lineHeight: 1.3,
    color: isHyperdriveDialog ? theme.palette.common.white : theme.palette.dark?.main ?? theme.palette.text.main,
  };
  const productDialogCloseIconSx = {
    color: isHyperdriveDialog ? theme.palette.common.white : theme.palette.grey[600],
  };
  const productDialogBodyTextSx = {
    color: isHyperdriveDialog ? "rgba(255, 255, 255, 0.88)" : theme.palette.grey[600],
    fontSize: "0.9375rem",
    lineHeight: 1.55,
  };

  return (
    <CommonLayout>
      <Backdrop
        open={pageLoading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} position="relative">
        {/* Header: back, title, status */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              sx={{ cursor: "pointer" }}
              onClick={() => requestNavigateAway("/hyperwolf/products")}
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <ArrowBackRoundedIcon fontSize="small" />
              </IconButton>

            </Stack>
            <Typography variant="h6" fontWeight="600" ml={1} color="#2E2E3A">
              {isEditMode ? "Edit Product" : "Add Product"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1.25}>
            <Typography variant="body1" fontWeight="500" color="text.secondary">
              Status
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{
                p: 0.75,
                borderRadius: 2,
                bgcolor: "#F2F3F7",
              }}
            >
              <MDButton
                type="button"
                onClick={() => {
                  setFormData((p) => ({ ...p, status: "Active" }));
                }}
                variant={formData.status === "Active" ? "contained" : "text"}
                color={formData.status === "Active" ? "success" : "dark"}
                size="small"
                sx={{
                  minWidth: "92px",
                  borderRadius: 1.75,
                  minHeight: "28px",
                  p: 0.25,
                  fontWeight: 500,
                }}
              >
                Active
              </MDButton>
              <MDButton
                type="button"
                onClick={() => {
                  setFormData((p) => ({ ...p, status: "Inactive" }));
                }}
                variant={formData.status === "Inactive" ? "contained" : "text"}
                color={formData.status === "Inactive" ? "error" : "dark"}
                size="small"
                sx={{
                  minWidth: "92px",
                  borderRadius: 1.75,
                  minHeight: "28px",
                  p: 0.25,
                  fontWeight: 500,
                }}
              >
                Inactive
              </MDButton>
            </Stack>
          </Box>
        </Stack>

        <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
          {/* Product Images */}
          <Typography variant="body2" fontWeight="600" mb={1} color="#2E2E3A">
            Product Images
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1.25} mb={4.5}>
            {formData.productImages.length > 0 && (
              <ImageDragger
                images={formData.productImages.map((item) => (item?.preview ?? item))}
                imageDragHandler={productImageReorderHandler}
                removeImageHandler={removeImageHandler}
              />
            )}
            <Box
              component="label"
              htmlFor="hyperwolf-product-image-input"
              sx={{
                cursor: "pointer",
                minWidth: 160,
                minHeight: 160,
                borderRadius: "20px",
                border: "2px dashed #d0d0d0",
                bgcolor: "#F1F1F1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                p: 2,
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 32, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight="500" textAlign="center" mt={0.5}>
                Add {formData.productImages.length > 0 ? "More" : ""}
              </Typography>
            </Box>
            <input
              id="hyperwolf-product-image-input"
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={productImageUploadHandler}
            />
          </Stack>

          {/* Product Info */}
          <Typography variant="body2" fontWeight="600" mb={1} color="#2E2E3A">
            Product Info
          </Typography>
          <Grid container spacing={2.5} mb={4.5}>
            {/* Row 1: Product Name | Category | Brand | Distributor */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                label="Product Name"
                variant="outlined"
                className="form-input"
                sx={productInfoFieldSx}
                required
                error={Boolean(errors.productName)}
                helperText={errors.productName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl
                fullWidth
                size="small"
                sx={productInfoFieldSx}
                error={Boolean(errors.categoryId)}
                required
              >
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  label="Category *"
                  IconComponent={ExpandMoreIcon}
                  renderValue={(v) => {
                    if (!v) return "";
                    const c = categories.find(
                      (x) => (x.categoryId || x.id || x._id) === v
                    );
                    return c?.name ?? c?.categoryName ?? v;
                  }}
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem
                      key={cat.categoryId || cat.id || cat._id}
                      value={cat.categoryId || cat.id || cat._id}
                    >
                      {cat.name ?? cat.categoryName ?? "-"}
                    </MenuItem>
                  ))}
                </Select>
                {errors.categoryId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl
                fullWidth
                size="small"
                sx={productInfoFieldSx}
                error={Boolean(errors.brandId)}
                required
              >
                <InputLabel id="brand-label">Brand</InputLabel>
                <Select
                  labelId="brand-label"
                  name="brandId"
                  value={formData.brandId}
                  onChange={handleChange}
                  label="Brand *"
                  IconComponent={ExpandMoreIcon}
                  renderValue={(v) => {
                    if (!v) return "";
                    const b = brands.find((x) => (x.brandId || x._id || x.id) === v);
                    return b?.brandName ?? b?.name ?? v;
                  }}
                >
                  <MenuItem value="">Select Brand</MenuItem>
                  {brands.map((brand) => (
                    <MenuItem
                      key={brand.brandId || brand._id || brand.id}
                      value={brand.brandId || brand._id || brand.id}
                    >
                      {brand.brandName ?? brand.name ?? "-"}
                    </MenuItem>
                  ))}
                </Select>
                {errors.brandId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.brandId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl
                fullWidth
                size="small"
                sx={productInfoFieldSx}
                error={Boolean(errors.distributorId)}
                required
              >
                <InputLabel id="distributor-label">Distributor</InputLabel>
                <Select
                  labelId="distributor-label"
                  name="distributorId"
                  value={formData.distributorId}
                  onChange={handleChange}
                  label="Distributor *"
                  IconComponent={ExpandMoreIcon}
                  renderValue={(v) => {
                    if (!v) return "";
                    const d = distributorsList.find((x) => x.blazeVendorId === v);
                    return d?.distributorName ?? d?.name ?? v;
                  }}
                >
                  <MenuItem value="">Select Distributor</MenuItem>
                  {distributorsList
                    .filter((dist) => dist.blazeVendorId)
                    .map((dist) => (
                      <MenuItem
                        key={dist.blazeVendorId}
                        value={dist.blazeVendorId}
                      >
                        {dist.distributorName ?? dist.name ?? "-"}
                      </MenuItem>
                    ))}
                </Select>
                {errors.distributorId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.distributorId}
                  </Typography>
                )}
              </FormControl>
            </Grid>


            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={productInfoFieldSx}>
                <InputLabel id="wpu-label">Weight Per Unit (WPU)</InputLabel>
                <Select
                  labelId="wpu-label"
                  name="weightPerUnit"
                  value={formData.weightPerUnit}
                  onChange={handleChange}
                  label="Weight Per Unit (WPU)"
                  IconComponent={ExpandMoreIcon}
                >
                  <MenuItem value="">Select</MenuItem>
                  {WPU_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Row 2: Weight (when CUSTOM_GRAMS) | Weight Per Unit (WPU) | Custom Gram Type | Retail Price | Sell Type | Cannabis Type */}
            {formData.weightPerUnit === "CUSTOM_GRAMS" && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  name="customWeight"
                  value={formData.customWeight}
                  onChange={handleChange}
                  label="Custom weight"
                  placeholder="e.g. 3.5 or 3.5 g"
                  type="text"
                  variant="outlined"
                  className="form-input"
                  sx={productInfoFieldSx}
                  error={Boolean(errors.customWeight)}
                  helperText={errors.customWeight}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={productInfoFieldSx}>
                <InputLabel id="custom-gram-type-label">Custom Gram Type</InputLabel>
                <Select
                  labelId="custom-gram-type-label"
                  name="customGramType"
                  value={formData.customGramType}
                  onChange={handleChange}
                  label="Custom Gram Type"
                  IconComponent={ExpandMoreIcon}
                >
                  {CUSTOM_GRAM_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                name="retailPrice"
                value={formData.retailPrice}
                onChange={handleChange}
                label="Retail Price"
                type="number"
                variant="outlined"
                className="form-input"
                sx={productInfoFieldSx}
                error={Boolean(errors.retailPrice)}
                helperText={errors.retailPrice}
                inputProps={{ min: 0, step: "any" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={productInfoFieldSx}>
                <InputLabel id="sell-type-label">Sell Type</InputLabel>
                <Select
                  labelId="sell-type-label"
                  name="sellType"
                  value={formData.sellType}
                  onChange={handleChange}
                  label="Sell Type"
                  IconComponent={ExpandMoreIcon}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Both">Both</MenuItem>
                  <MenuItem value="Recretional">Recretional</MenuItem>
                  <MenuItem value="Medical">Medical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" sx={productInfoFieldSx}>
                <InputLabel id="cannabis-type-label">Cannabis Type</InputLabel>
                <Select
                  labelId="cannabis-type-label"
                  name="cannabisType"
                  value={formData.cannabisType}
                  onChange={handleChange}
                  label="Cannabis Type"
                  IconComponent={ExpandMoreIcon}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Indica">Indica</MenuItem>
                  <MenuItem value="Sativa">Sativa</MenuItem>
                  <MenuItem value="Hybrid">Hybrid</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}

            {/* Row 3: Strain Type | THC (mg/%) | SKU | empty */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="strain-type-label">Strain Type</InputLabel>
                <Select
                  labelId="strain-type-label"
                  name="strain"
                  value={formData.strain}
                  onChange={handleChange}
                  label="Strain Type"
                  className="form-select"
                  sx={productInfoFieldSx}
                  IconComponent={ExpandMoreIcon}
                >
                  {strainTypeOptions.map((infoEffect) => (
                    <MenuItem key={infoEffect.strainType} value={infoEffect.strainType}>
                      {infoEffect?.strainType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                name="thcMgPercent"
                value={formData.thcMgPercent}
                onChange={handleChange}
                label="THC (mg/%)"
                variant="outlined"
                className="form-input"
                sx={productInfoFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                label="SKU"
                variant="outlined"
                className="form-input"
                sx={productInfoFieldSx}
              />
            </Grid>

            {/* Tags - full width, inline chips */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                filterSelectedOptions
                options={tagOptions}
                value={formData.tags}
                inputValue={tagInput}
                onInputChange={handleTagsInputChange}
                onChange={handleTagsChange}
                onKeyDown={handleTagKeyDown}
                filterOptions={filterTagOptions}
                loading={tagSearchLoading}
                loadingText="Searching tags…"
                noOptionsText={tagInput.trim() ? "Press Enter to add this tag" : "Type to search tags"}
                forcePopupIcon
                popupIcon={<ExpandMoreIcon />}
                clearOnBlur={false}
                handleHomeEndKeys
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                      label={option}
                      size="small"
                      deleteIcon={<CloseRoundedIcon fontSize="small" />}
                    />
                  ))
                }
                renderOption={(props, option) => {
                  const trimmedInput = normalizeTagValue(tagInput);
                  const isNew =
                    trimmedInput !== "" &&
                    String(option).toLowerCase() === trimmedInput.toLowerCase() &&
                    !tagOptions.some(
                      (o) => String(o).toLowerCase() === trimmedInput.toLowerCase()
                    );
                  return (
                    <li {...props}>
                      {isNew ? (
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <AddRoundedIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            Add &quot;{option}&quot;
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2">{option}</Typography>
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    label="Tags"
                    placeholder={
                      formData.tags.length > 0 ? "Add more…" : "Search or type to add tags…"
                    }
                    variant="outlined"
                    className="form-input"
                  />
                )}
                ListboxProps={{
                  sx: { maxHeight: 240, py: 0.5 },
                }}
                sx={tagsAutocompleteSx}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5, display: "block" }}>
                Type to search, press Enter or comma to add
                {formData.tags.length > 0 ? ` • ${formData.tags.length} selected` : ""} — click × on a tag to remove.
              </Typography>
            </Grid>
          </Grid>

          {/* Description */}
          <Typography variant="body2" fontWeight="600" mb={1} color="#2E2E3A">
            Description
          </Typography>
          <Box mb={4.5}>
            <CommonQuillEditor
              value={formData.description}
              onChange={(html) => {
                setFormData((p) => ({ ...p, description: html }));
              }}
              height="280px"
            />
          </Box>
        </Card>

        {/* FAQs Section - separate card, same as Add Brand */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2.5, boxShadow: "none" }}>
          <Typography variant="h5" fontWeight="semibold" color="text.primary" mb={3}>
            FAQs Section
          </Typography>
          <Typography variant="body2" fontWeight="600" color="secondary" mb={1}>
            Section Title (H2)
          </Typography>
          <TextField
            fullWidth
            value={formData.faqSectionTitle}
            name="faqSectionTitle"
            onChange={handleChange}
            label="Faq's"
            variant="outlined"
            size="small"
            sx={{ mb: 3 }}
          />

          {formData.faqs.map((faq, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.300",
                bgcolor: "grey.50",
                position: "relative",
              }}
            >
              <Box display="flex" justifyContent="flex-end" mb={1}>
                <MDButton
                  variant="text"
                  size="small"
                  onClick={() => removeFaq(index)}
                  // disabled={formData.faqs.length <= 1}
                  sx={{
                    color: "#c62828",
                    minWidth: "auto",
                    "&:hover": { bgcolor: "rgba(198, 40, 40, 0.08)" },
                  }}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" style={{ marginRight: 4 }} />
                  Delete
                </MDButton>
              </Box>
              <Typography variant="body2" fontWeight="600" color="secondary" mb={1}>
                Question
              </Typography>
              <CommonQuillEditor
                value={faq.question}
                onChange={(html) => updateFaq(index, "question", html)}
                height="120px"
              />
              <Typography variant="body2" fontWeight="600" color="secondary" mt={2} mb={1}>
                Answer
              </Typography>
              <CommonQuillEditor
                value={faq.answer}
                onChange={(html) => updateFaq(index, "answer", html)}
                height="120px"
              />
            </Box>
          ))}

          <Box display="flex" justifyContent="flex-start" mb={2}>
            <MDButton
              variant="outlined"
              color="info"
              size="medium"
              onClick={addFaq}
              startIcon={<AddRoundedIcon />}
              sx={{
                bgcolor: "white",
                borderColor: "info.main",
                color: "info.main",
                fontWeight: 700,
                borderRadius: 10,
                "&:hover": {
                  bgcolor: "grey.50",
                  borderColor: "info.dark",
                  color: "info.dark",
                },
              }}
            >
              Add FAQ
            </MDButton>
          </Box>
        </Card>

        {/* Meta Properties & Content Section - separate card */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2.5, boxShadow: "none" }}>
          <Typography variant="h5" fontWeight="semibold" color="text.primary" mb={2}>
            Meta Properties
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            gap={2}
            mb={3}
          >
            <Alert severity="info" sx={{ flex: 1, mb: { xs: 0, sm: 0 } }}>
              Product Name, Category Name, Brand Name, Strain Type, Product Weight (Value &amp; Unit), and THC (Value &amp; Unit) are required.
            </Alert>

            <MDButton
              variant="contained"
              color="primary"
              onClick={handleGenerateMeta}
              disabled={!canGenerateMeta || generateLoading}
              startIcon={!generateLoading && <SmartToyIcon />}
              sx={{ minWidth: 160, alignSelf: { xs: "flex-end", sm: "center" } }}
            >
              {generateLoading ? <CircularProgress size={20} color="inherit" /> : "Generate"}
            </MDButton>
          </Stack>
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleChange}
                label="Meta Title"
                variant="outlined"
                sx={productInfoFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                size="small"
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                label="Description"
                variant="outlined"
                sx={roundedMultilineSx}
              />
            </Grid>
          </Grid>

          <Typography variant="h5" fontWeight="semibold" color="text.primary" mb={2}>
            Content Section
          </Typography>
          <Box sx={{ "& .quill-text-editor": { borderRadius: "10rem" } }}>
            <CommonQuillEditor
              value={formData.content}
              onChange={(html) => {
                setFormData((p) => ({ ...p, content: html }));
              }}
              height="280px"
            />
          </Box>


        </Card>
        <Box>
          {/* Button Section - same as Add Brand */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={4} mb={2}>
            <MDButton
              sx={{ minWidth: "120px" }}
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSaveProduct}
              disabled={saveInProgress}
            >
              {saveInProgress ? "Saving…" : "Save"}
            </MDButton>

            <MDButton
              sx={{ minWidth: "120px" }}
              type="button"
              variant="outlined"
              color="dark"
              onClick={() => requestNavigateAway("/hyperwolf/products")}
            >
              Cancel
            </MDButton>
          </Stack>
        </Box>
      </Box>

      <Dialog
        className={productFormDialogClassName}
        open={saveConfirmOpen}
        onClose={() => !saveInProgress && setSaveConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="hyperwolf-product-save-dialog-title"
        BackdropProps={{ sx: productDialogBackdropSx }}
        PaperProps={{ elevation: 0, sx: productDialogPaperSx }}
      >
        <DialogTitle id="hyperwolf-product-save-dialog-title" component="div" sx={productDialogTitleSx}>
          Save product
          <IconButton
            size="small"
            edge="end"
            aria-label="Close"
            disabled={saveInProgress}
            onClick={() => setSaveConfirmOpen(false)}
            sx={productDialogCloseIconSx}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          <DialogContentText component="div" sx={productDialogBodyTextSx}>
            {isEditMode
              ? "Save your changes to this product?"
              : "Create this product with the information you entered?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5, flexWrap: "wrap" }}>
          <MDButton
            type="button"
            size="small"
            color="secondary"
            disabled={saveInProgress}
            onClick={() => setSaveConfirmOpen(false)}
          >
            Cancel
          </MDButton>
          <MDButton
            type="button"
            size="small"
            color="primary"
            disabled={saveInProgress}
            onClick={confirmSaveProduct}
          >
            {saveInProgress ? "Saving…" : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </CommonLayout>
  );
}
